import React, {
  FC,
  useCallback,
  useEffect,
  useMemo,
  useState,
  forwardRef,
  useImperativeHandle,
} from 'react'
import {SubClient} from '../../../../../services/cellcast.v2.types'
import {
  useAllocateSubClientBusinessNumberMutation,
  useLazyGetAllocatedSubClientBusinessNumbersQuery,
  useRemoveAllocatedSubClientBusinessNumberMutation,
  useSetSubClientDefaultBusinessNumberMutation,
} from '../../../../../services/cellcast.v2'
import {Modal, OverlayTrigger, Spinner, Tooltip} from 'react-bootstrap'
import NodataYet from '../../../../Components/NodataYet'
import {KTSVG} from '../../../../../_metronic/helpers'
import WSButton from '../../../../../_metronic/helpers/components/WSButton/WSButton'

import {toast} from 'react-toastify'
import Select from 'react-select'
import DeleteConfirmationPopup from '../../../../Components/DeleteConfirmationPopup'
import {BusinessNumber} from '../../../../../services/cellcast.type'
import {useLazyGetBusinessNumberQuery} from '../../../../../services/cellcast'
import DefaultActionButton from '../../../../Components/DefaultActionButton'

type SenderBusinessNumberProps = {
  data: SubClient | undefined
  onDefaultChange?: () => void
}

export type SenderBusinessNumberRef = {
  refreshData: () => void
}

const SenderBusinessNumber = forwardRef<SenderBusinessNumberRef, SenderBusinessNumberProps>(
  (props, ref) => {
    const {data, onDefaultChange} = props
    const [fetchDnFn, {data: fetchDnData, isLoading: fetchDnLoading, isFetching: fetchDnFetching}] =
      useLazyGetAllocatedSubClientBusinessNumbersQuery()
    const [removeAllocateFn, {isLoading: removeAllocateLoading}] =
      useRemoveAllocatedSubClientBusinessNumberMutation()

    const [showModalAllocate, setShowModalAllocate] = useState(false)
    const [showModalRemoveAllocate, setShowModalRemoveAllocate] = useState(false)
    const [selectedRemove, setSelectedRemove] = useState<BusinessNumber>()

    // Expose refresh function to parent via ref
    useImperativeHandle(
      ref,
      () => ({
        refreshData: () => {
          if (data && data._id) {
            fetchDnFn({id: data._id})
          }
        },
      }),
      [data, fetchDnFn]
    )

    useEffect(() => {
      if (data && data._id) {
        fetchDnFn({
          id: data._id,
        })
      }
    }, [data])

    const AllocateModalComponent = useMemo(
      () => (
        <AllocateModal
          show={showModalAllocate}
          handleClose={() => {
            setShowModalAllocate(false)
          }}
          subClient={data}
          selectedIDs={fetchDnData?.data ? fetchDnData.data.map((el) => el._id) : []}
          callbackFn={() => {
            setShowModalAllocate(false)
            fetchDnFn({id: data?._id || ''})
          }}
        />
      ),
      [data, showModalAllocate]
    )

    const DeleteModalComponent = useMemo(
      () => (
        <DeleteConfirmationPopup
          show={showModalRemoveAllocate && selectedRemove !== undefined}
          handleClose={() => {
            setShowModalRemoveAllocate(false)
            setSelectedRemove(undefined)
          }}
          Message={` To confirm that you want to remove ${selectedRemove?.businessname}, please type "DELETE" in the field provided below`}
          confirm={() => {
            removeAllocateFn({
              businessNumberId: selectedRemove?._id || '',
              id: data?._id || '',
            })
              .then((res) => {
                if ('data' in res) {
                  toast.success(res.data?.message)
                } else if ('error' in res) {
                  toast.error(res.error)
                }
                fetchDnFn({id: data?._id || ''})
              })
              .catch((err) => {
                toast.error(err.error)
              })
              .finally(() => {
                setSelectedRemove(undefined)
              })
          }}
          withInputConfirmation={true}
        />
      ),
      [data?._id, selectedRemove, showModalRemoveAllocate]
    )

    const bgColor: any = {
      REQUESTED: 'warning',
      ACTIVE: 'success',
      DENIED: 'danger',
    }

    return (
      <div className='col-md-12 mb-5'>
        {AllocateModalComponent}
        {DeleteModalComponent}
        <div className='card p-8'>
          <div className='card-header border-0 p-0 align-items-center mb-5'>
            <h3 className='card-title align-items-start  flex-column'>
              <span className='card-label fw-bolder fs-2 mb-1'>Business Name (Alpha ID)</span>
            </h3>
            <div className='card-toolbar'>
              <button
                type='button'
                className='btn btn-primary ms-5'
                onClick={() => setShowModalAllocate(true)}
              >
                <KTSVG path='/media/icons/duotune/arrows/arr075.svg' className='svg-icon-3' />
                Allocate
              </button>
            </div>
          </div>
          {fetchDnLoading ? (
            <div className='row flex-center h-200'>
              <Spinner />
            </div>
          ) : (
            <>
              {fetchDnData?.data && fetchDnData.data.length > 0 ? (
                <div className='card-body p-0 pt-1'>
                  <div className='table-responsive'>
                    <table className='table align-middle gs-0 gy-5 all-message-table'>
                      <thead>
                        <tr className='fw-400 text-muted'>
                          <th className='min-w-140px'>Name</th>
                          <th className='min-w-140px'>Status</th>
                          <th className='min-w-150px'>Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {fetchDnFetching ? (
                          <tr>
                            <td colSpan={3} className='text-center'>
                              <Spinner />
                            </td>
                          </tr>
                        ) : (
                          <>
                            {fetchDnData?.data?.map((item, i: number) => (
                              <React.Fragment key={i}>
                                <tr className='border-dashed border-2 border-bottom'>
                                  <td className='top-align'>{item.businessname || '--'}</td>
                                  <td>
                                    <span
                                      className={`badge badge-light-${bgColor?.[item?.status]}`}
                                      style={{textTransform: 'capitalize'}}
                                    >
                                      {item?.status}{' '}
                                    </span>{' '}
                                  </td>
                                  <td className='top-align'>
                                    <div className='d-flex justify-content-end gap-2'>
                                      {item.status === 'ACTIVE' && (
                                        <DefaultNumberButton
                                          item={item}
                                          subClientId={data?._id || ''}
                                          onUpdate={() => {
                                            fetchDnFn({id: data?._id || ''})
                                            onDefaultChange?.()
                                          }}
                                        />
                                      )}
                                      <OverlayTrigger
                                        placement='bottom'
                                        delay={{show: 250, hide: 400}}
                                        overlay={<Tooltip id='tooltip-bottom'>Remove</Tooltip>}
                                      >
                                        <span
                                          className='btn btn-sm btn-icon btn-bg-light btn-active-color-danger'
                                          style={{width: '32px', height: '32px'}}
                                          onClick={() => {
                                            setSelectedRemove(item)
                                            setShowModalRemoveAllocate(true)
                                          }}
                                        >
                                          <i className='bi bi-trash fs-5 text-danger'></i>
                                        </span>
                                      </OverlayTrigger>
                                    </div>
                                  </td>
                                </tr>
                              </React.Fragment>
                            ))}
                          </>
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>
              ) : (
                <NodataYet message='No business number allocated (yet!)' />
              )}
            </>
          )}
        </div>
      </div>
    )
  }
)

export type AllocateModalProps = {
  show: boolean
  subClient: SubClient | undefined
  selectedIDs: string[]
  handleClose: () => void
  callbackFn: () => void
}

const AllocateModal: FC<AllocateModalProps> = (props) => {
  const {subClient, selectedIDs, show, handleClose, callbackFn} = props

  const [fetchNumber, {data: numbers, isLoading: numberLoading, isFetching: numberFetching}] =
    useLazyGetBusinessNumberQuery()
  const [allocateDn, {isLoading: allocateLoading}] = useAllocateSubClientBusinessNumberMutation()

  const [selectedNumber, setSelectedDn] = useState('')
  const [selectedNumberOptions, setSelectedDnOptions] = useState([
    {
      label: 'select business number',
      value: '',
    },
  ])

  useEffect(() => {
    if (show) {
      fetchNumber(null)
    }
  }, [fetchNumber, show])

  useEffect(() => {
    if (numbers && numbers.data && Array.isArray(numbers.data)) {
      let options: any = numbers.data.filter(
        (el) => !selectedIDs.includes(el._id) && el.status === 'ACTIVE'
      )
      options = options.map((el: any) => ({
        label: el?.businessname || '--',
        value: el._id,
      }))

      setSelectedDnOptions([
        {
          label: 'select business number',
          value: '',
        },
        ...options,
      ])
    }
  }, [numbers, selectedIDs])

  const handleSubmit = useCallback(() => {
    if (!subClient || (subClient && !subClient._id)) {
      return
    }

    allocateDn({
      businessNumberId: selectedNumber,
      id: subClient?._id,
    })
      .then((res) => {
        if ('data' in res) {
          toast.success(res.data?.message)
          if (callbackFn) {
            setSelectedDn('')
            callbackFn()
          }
        } else if ('error' in res) {
          const errObj = res.error as any
          if (errObj.data.message) {
            toast.error(errObj.data.message)
          } else {
            toast.error(res.error)
          }
        }
      })
      .catch((err) => {
        toast.error(err.error)
      })
  }, [allocateDn, callbackFn, selectedNumber, subClient])

  return (
    <Modal
      id='kt_modal_create_app'
      tabIndex={-1}
      aria-hidden='true'
      dialogClassName='modal-dialog-centered mw-700px h-auto'
      show={show}
      onHide={handleClose}
    >
      <div className='scroll-x'>
        <div className='modal-header'>
          <h2 className='fs-2'>Allocate Business Number</h2>
          <div
            className='btn btn-icon btn-sm'
            onClick={() => {
              handleClose()
            }}
          >
            <KTSVG className='svg-icon-1' path='/media/icons/duotune/arrows/arr061.svg' />
          </div>
        </div>
        <div className='modal-body py-lg-5 px-lg-5 border-bottom'>
          <div className='mb-10 container'>
            <label className='form-label required'>Business Number</label>
            <Select
              name='status'
              value={
                selectedNumberOptions.find((el) => el.value === selectedNumber) ||
                selectedNumberOptions[0]
              }
              className='form-select form-select-solid fw-bolder'
              onChange={(e: any) => setSelectedDn(e.value)}
              options={selectedNumberOptions}
            />
          </div>

          <div className='container d-flex align-items-end justify-content-end mt-6 mb-5'>
            <WSButton
              type='submit'
              className='btn btn-lg btn-light-primary me-5 fw-500'
              onClick={() => {
                handleClose()
              }}
            >
              <span className='indicator-label'>Cancel</span>
            </WSButton>
            <WSButton
              disabled={numberLoading || numberFetching || allocateLoading}
              type='submit'
              className='btn btn-lg btn-primary fw-500'
              onClick={() => handleSubmit()}
            >
              <span className='indicator-label'>
                {allocateLoading ? <Spinner animation='grow' size='sm' /> : 'Submit'}
              </span>
            </WSButton>
          </div>
        </div>
      </div>
    </Modal>
  )
}

// Default Number Button Component for Parent Account Management
type DefaultNumberButtonProps = {
  item: BusinessNumber
  subClientId: string
  onUpdate: () => void
}

const DefaultNumberButton: FC<DefaultNumberButtonProps> = ({item, subClientId, onUpdate}) => {
  const [setDefaultNumber, {isLoading}] = useSetSubClientDefaultBusinessNumberMutation()

  const isDefault = Boolean(item.subClientDefaults && item.subClientDefaults[subClientId] === true)

  const handleSetDefault = useCallback(async () => {
    try {
      const response = await setDefaultNumber({
        id: subClientId,
        numberId: item._id,
        isDefault: !isDefault,
      }).unwrap()

      if (response.status) {
        onUpdate()
      }
    } catch (error: any) {
      toast.error(error.data?.message || 'Failed to set default number')
    }
  }, [item._id, subClientId, setDefaultNumber, onUpdate, isDefault])

  return (
    <DefaultActionButton isDefault={isDefault} isLoading={isLoading} onClick={handleSetDefault} />
  )
}

export default SenderBusinessNumber
